import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useAuthStore } from './stores/authStore';
import { canSeeHr } from './config/modulePermissions';
import MainLayout from './components/layout/MainLayout';
import PickUserLayout from './components/layout/PickUserLayout';
import PackUserLayout from './components/layout/PackUserLayout';
import InboundUserLayout from './components/layout/InboundUserLayout';
import Dashboard from './pages/dashboard/Dashboard';
import LoginPage from './pages/auth/LoginPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useEffect } from 'react';
import sessionManager from './utils/sessionManager';
import { PrintDiagnostics } from './utils/printDiagnostics';

// Module imports - using default exports
import SalesOverview from './modules/sales/pages/SalesOverview';
import ReturnManagement from './modules/sales/pages/ReturnManagement';
import CRMOverview from './modules/crm/pages/CRMOverview';
import AccountingOverview from './modules/accounting/pages/AccountingOverview';
import InventoryOverview from './modules/inventory/pages/InventoryOverview';
import InventoryDashboardPage from './modules/inventory/pages/InventoryDashboardPage';
import InboundManagement from './modules/inventory/pages/InboundManagement';
import StockManagement from './modules/inventory/pages/StockManagement';
import PickingManagement from './modules/inventory/components/PickingManagement';
import PackingManagement from './modules/inventory/components/PackingManagement';
import InboundBulkUploadPage from './modules/inventory/pages/InboundBulkUploadPage';

import ProductMasterSetup from './modules/inventory/pages/ProductMasterSetup';
import { ProcurementOverview } from './modules/procurement/pages/ProcurementOverview';
import HROverview from './modules/hr/pages/HROverview';
import ManufacturingOverview from './modules/manufacturing/pages/ManufacturingOverview';
import ReportsOverview from './modules/reports/pages/ReportsOverview';
import SettingsOverview from './modules/settings/pages/SettingsOverview';

import UserManagement from './pages/settings/UserManagement';
import { ImportExportPage } from './pages/settings/ImportExport';
import AuditLog from './pages/settings/AuditLog';
import OutboundOrderPage from './modules/inventory/pages/OutboundOrderPage';
import InternalTransferPage from './modules/inventory/pages/InternalTransferPage';
import WarehouseManagement from './modules/inventory/pages/NewWarehouseManagement';
import TestCustomization from './pages/TestCustomization';
import FedExSettings from './modules/inventory/pages/FedExSettings';
import SFExpressSettings from './modules/inventory/pages/SFExpressSettings';
import SFExpressB2BSettings from './modules/inventory/pages/SFExpressB2BSettings';

// Import i18n configuration
import './i18n/config';
import HKTVmallJobAndStatus from './modules/integrations/hktvmall/HKTVmallJobAndStatus';
import IntegrationsOverview from './modules/integrations/IntegrationsOverview';
import ShopifyOverview from './modules/integrations/shopify/ShopifyOverview';

// WYNI AI brand theme
const themeConfig = {
  token: {
    colorPrimary: '#005b96',
    colorPrimaryHover: '#3066be',
    colorPrimaryActive: '#03396c',
    colorPrimaryText: '#ffffff',
    colorText: '#011f4b',
    colorTextSecondary: '#03396c',
    colorTextTertiary: '#005b96',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBorder: '#d9d9d9',
    colorBorderSecondary: '#f0f0f0',
    borderRadius: 6,
    fontSize: 14,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    lineHeight: 1.5,
  },
  components: {
    Button: {
      colorPrimary: '#005b96',
      colorPrimaryHover: '#3066be',
      colorPrimaryActive: '#03396c',
      colorPrimaryText: '#ffffff',
      colorPrimaryTextHover: '#ffffff',
      colorPrimaryTextActive: '#ffffff',
      colorText: '#011f4b',
      colorTextHover: '#005b96',
      colorBgContainer: '#ffffff',
      colorBgContainerHover: '#f5f5f5',
      borderColorDisabled: '#d9d9d9',
      colorTextDisabled: '#bfbfbf',
    },
    Menu: {
      colorPrimary: '#005b96',
      colorBgContainer: '#001529',
      colorText: '#ffffff',
      colorTextHover: '#ffffff',
      colorItemBg: 'transparent',
      colorItemBgHover: '#3066be',
      colorItemBgSelected: '#005b96',
      colorItemText: '#ffffff',
      colorItemTextHover: '#ffffff',
      colorItemTextSelected: '#ffffff',
    },
    Card: {
      colorBgContainer: '#ffffff',
      colorBorderSecondary: '#f0f0f0',
      colorText: '#011f4b',
      colorTextHeading: '#011f4b',
    },
    Typography: {
      colorText: '#011f4b',
      colorTextHeading: '#011f4b',
      colorTextSecondary: '#03396c',
      colorTextDescription: '#005b96',
    },
    Table: {
      colorBgContainer: '#ffffff',
      colorText: '#011f4b',
      colorTextHeading: '#011f4b',
      colorBorderSecondary: '#f0f0f0',
    },
    Input: {
      colorText: '#011f4b',
      colorBgContainer: '#ffffff',
      colorBorder: '#d9d9d9',
      colorPrimary: '#005b96',
      colorPrimaryHover: '#3066be',
    },
    Select: {
      colorText: '#011f4b',
      colorBgContainer: '#ffffff',
      colorBorder: '#d9d9d9',
      colorPrimary: '#005b96',
      colorPrimaryHover: '#3066be',
    },
    Modal: {
      colorBgElevated: '#ffffff',
      colorText: '#011f4b',
      colorTextHeading: '#011f4b',
    },
    Drawer: {
      colorBgElevated: '#ffffff',
      colorText: '#011f4b',
      colorTextHeading: '#011f4b',
    },
  },
};

/**
 * HR is Super Admin only, and the navigation hides it for everyone else. That
 * still leaves a bookmark or a typed URL, which used to render the HR screen
 * and then show "Failed to load HR data" once the server refused it — an
 * access rule that reads like a broken page. Anyone without access is sent to
 * the dashboard instead.
 */
const HRRoute = () => {
  const role = useAuthStore(state => state.user?.role);
  return canSeeHr(role) ? <HROverview /> : <Navigate to="/" replace />;
};

function App() {
  useEffect(() => {
    sessionManager.init();
    return () => sessionManager.stop();
  }, []);
  return (
    <ConfigProvider theme={themeConfig}>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* PICK角色用户的专用路由 */}
          <Route path="/pick" element={
            <ProtectedRoute requiredRole="PICK_SCANNING">
              <PickUserLayout />
            </ProtectedRoute>
          }>
            <Route index element={<PickingManagement />} />
            <Route path="inventory/picking" element={<PickingManagement />} />
          </Route>
          {/* PACK角色用户的专用路由 */}
          <Route path="/pack" element={
              <ProtectedRoute requiredRole="PACK_SCANNING">
                <PackUserLayout />
              </ProtectedRoute>
            }>
              <Route index element={<PackingManagement />} />
              <Route path="inventory/packing" element={<PackingManagement />} />
          </Route>

          {/* PICKUP角色用户的专用路由 */}
          <Route path="/pickup" element={
            <ProtectedRoute requiredRole="PICKUP_SCANNING">
              <PackUserLayout />
            </ProtectedRoute>
          }>
            <Route index element={<PackingManagement />} />
            <Route path="inventory/packing" element={<PackingManagement />} />
          </Route>

          {/* INBOUND_OPERATOR角色用户的专用路由 */}
          <Route path="/inbound" element={
            <ProtectedRoute requiredRole="INBOUND_OPERATOR">
              <InboundUserLayout />
            </ProtectedRoute>
          }>
            <Route index element={<InboundManagement />} />
            <Route path="inventory/inbound" element={<InboundManagement />} />
          </Route>

          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            
            {/* Sales Module */}
            <Route path="sales" element={<SalesOverview />} />
            <Route path="sales/orders" element={<SalesOverview />} />
            <Route path="sales/quotations" element={<SalesOverview />} />
            <Route path="sales/invoices" element={<SalesOverview />} />
            
            {/* CRM Module */}
            <Route path="crm" element={<CRMOverview />} />
            <Route path="crm/contacts" element={<CRMOverview />} />
            <Route path="crm/pipeline" element={<CRMOverview />} />
            <Route path="crm/leads" element={<CRMOverview />} />
            
            {/* Accounting Module */}
            <Route path="accounting" element={<AccountingOverview />} />
            <Route path="accounting/quotations" element={<AccountingOverview />} />
            <Route path="accounting/invoices" element={<AccountingOverview />} />
            <Route path="accounting/credit-notes" element={<AccountingOverview />} />
            <Route path="accounting/invoice-templates" element={<AccountingOverview />} />
            <Route path="accounting/ar-aging" element={<AccountingOverview />} />
            <Route path="accounting/customer-statement" element={<AccountingOverview />} />
            <Route path="accounting/vendors" element={<AccountingOverview />} />
            <Route path="accounting/vendor-bills" element={<AccountingOverview />} />
            <Route path="accounting/recurring-bills" element={<AccountingOverview />} />
            <Route path="accounting/ap-aging" element={<AccountingOverview />} />
            <Route path="accounting/vendor-statement" element={<AccountingOverview />} />
            <Route path="accounting/chart-of-accounts" element={<AccountingOverview />} />
            <Route path="accounting/journals" element={<AccountingOverview />} />
            <Route path="accounting/journal-entries" element={<AccountingOverview />} />
            <Route path="accounting/general-ledger" element={<AccountingOverview />} />
            <Route path="accounting/payments" element={<AccountingOverview />} />
            <Route path="accounting/bank-reconciliation" element={<AccountingOverview />} />
            <Route path="accounting/deferred-revenue" element={<AccountingOverview />} />
            <Route path="accounting/deferred-expense" element={<AccountingOverview />} />
            <Route path="accounting/reports" element={<AccountingOverview />} />
            <Route path="accounting/advanced" element={<AccountingOverview />} />
            <Route path="accounting/settings" element={<AccountingOverview />} />
            
            {/* Inventory Module */}
            <Route path="inventory" element={<InventoryOverview />} />
            <Route path="inventory/dashboard" element={<InventoryDashboardPage />} />
            <Route path="inventory/products" element={<ProductMasterSetup />} />
            {/*<Route path="inventory/products" element={<InventoryOverview />} />*/}
            <Route path="inventory/stock" element={<StockManagement />} />
            <Route path="inventory/stock-management" element={<StockManagement />} />

            <Route path="inventory/warehouses" element={<InventoryOverview />} />
            <Route path="inventory/inbound" element={<InboundManagement />} />
            <Route path="inventory/inbound/bulk-upload" element={<InboundBulkUploadPage />} />
            <Route path="inventory/outbound" element={<OutboundOrderPage />} />
            <Route path="inventory/picking" element={<PickingManagement />} />
            <Route path="inventory/packing" element={<PackingManagement />} />
            <Route path="inventory/transfer" element={<InternalTransferPage />} />
            <Route path="inventory/returns" element={<ReturnManagement />} />
            <Route path="inventory/warehouse-management" element={<WarehouseManagement />} />
            <Route path="inventory/schedule-jobs" element={<HKTVmallJobAndStatus />} />

            {/* Integrations - Single entry with tabs */}
            <Route path="integrations" element={<IntegrationsOverview />} />
            <Route path="integrations/hktvmall" element={<IntegrationsOverview />} />
            <Route path="integrations/hktvmall/*" element={<IntegrationsOverview />} />
            <Route path="integrations/shopify" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/issues" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/admin-accounts" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/orders" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/job" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/products" element={<ShopifyOverview />} />
            <Route path="integrations/shopify/missing" element={<ShopifyOverview />} />

            {/* Purchase Module */}
            <Route path="purchase" element={<ProcurementOverview />} />
            <Route path="purchase/requisitions" element={<ProcurementOverview />} />
            <Route path="purchase/orders" element={<ProcurementOverview />} />
            <Route path="purchase/vendors" element={<ProcurementOverview />} />
            <Route path="purchase/workflows" element={<ProcurementOverview />} />
            
            {/* HR Module */}
            <Route path="hr" element={<HRRoute />} />
            <Route path="hr/employees" element={<HRRoute />} />
            <Route path="hr/payroll" element={<HRRoute />} />
            <Route path="hr/attendance" element={<HRRoute />} />
            
            {/* Manufacturing Module */}
            <Route path="manufacturing" element={<ManufacturingOverview />} />
            <Route path="manufacturing/bom" element={<ManufacturingOverview />} />
            <Route path="manufacturing/work-orders" element={<ManufacturingOverview />} />
            <Route path="manufacturing/production-plans" element={<ManufacturingOverview />} />
            <Route path="manufacturing/quality" element={<ManufacturingOverview />} />
            <Route path="manufacturing/lines" element={<ManufacturingOverview />} />
            
            {/* Reports Module */}
            <Route path="reports" element={<ReportsOverview />} />
            <Route path="reports/sales" element={<ReportsOverview />} />
            <Route path="reports/financial" element={<ReportsOverview />} />
            <Route path="reports/inventory" element={<ReportsOverview />} />
            <Route path="reports/hr" element={<ReportsOverview />} />
            <Route path="reports/manufacturing" element={<ReportsOverview />} />
            <Route path="reports/crm" element={<ReportsOverview />} />
            <Route path="reports/custom" element={<ReportsOverview />} />
            
            {/* Settings Module */}
            <Route path="settings" element={<SettingsOverview />} />
            <Route path="settings/general" element={<SettingsOverview />} />
            <Route path="settings/company" element={<SettingsOverview />} />
            <Route path="settings/users" element={<UserManagement />} />
            <Route path="settings/security" element={<SettingsOverview />} />
            <Route path="settings/integrations" element={<SettingsOverview />} />
            <Route path="settings/notifications" element={<SettingsOverview />} />
            <Route path="settings/backup" element={<SettingsOverview />} />
            <Route path="settings/audit" element={<AuditLog />} />
            <Route path="settings/preferences" element={<SettingsOverview />} />
            <Route path="settings/advanced" element={<SettingsOverview />} />
            <Route path="settings/permissions" element={<div>Permissions (Coming Soon)</div>} />
            <Route path="settings/import-export" element={<ImportExportPage />} />
            <Route path="settings/fedex" element={<FedExSettings />} />
            <Route path="settings/sf-express" element={<SFExpressSettings />} />
            <Route path="settings/sf-express-b2b" element={<SFExpressB2BSettings />} />

            {/* Test Customization Features */}
            <Route path="test-customization" element={<TestCustomization />} />
          </Route>
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
